import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../components/auth/AuthContext';
import { deriveRoles, EMPTY_RAW_ROLES, type RawRoles, type Roles } from './deriveRoles';
import { loadRawRoles } from './loadRoles';

export interface RolesValue extends Roles {
  loading: boolean;
  positionsError: string | null;
  refresh: () => Promise<void>;
}

const RolesContext = createContext<RolesValue | null>(null);

/**
 * Loads the signed-in user's profile flags, active staff positions and Keystone
 * membership ONCE per session (re-loads only when the user changes or
 * refresh() is called) and shares them with every useRoles() caller (#13).
 * UI gating only: RLS and the SQL helpers remain the security boundary.
 */
export function RolesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const uid = user?.uid;
  const [raw, setRaw] = useState<RawRoles>(EMPTY_RAW_ROLES);
  const [positionsError, setPositionsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // The uid the current `raw` belongs to, so a user change never shows the
  // previous user's roles (or a not-loading empty set) for a render.
  const [loadedUid, setLoadedUid] = useState<string | undefined>(undefined);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    if (!uid) {
      setRaw(EMPTY_RAW_ROLES);
      setPositionsError(null);
      setLoadedUid(undefined);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { positionsError: err, ...next } = await loadRawRoles(uid);
    if (id !== requestId.current) return; // a newer load superseded this one
    setRaw(next);
    setPositionsError(err);
    setLoadedUid(uid);
    setLoading(false);
  }, [uid]);

  useEffect(() => {
    load();
    const requests = requestId;
    return () => {
      requests.current++; // drop any in-flight load for the previous user
    };
  }, [load]);

  const value = useMemo<RolesValue>(
    () => ({
      ...deriveRoles(loadedUid === uid ? raw : EMPTY_RAW_ROLES),
      loading: loading || loadedUid !== uid,
      positionsError,
      refresh: load,
    }),
    [raw, loading, loadedUid, uid, positionsError, load],
  );

  return <RolesContext.Provider value={value}>{children}</RolesContext.Provider>;
}

export function useRoles(): RolesValue {
  const ctx = useContext(RolesContext);
  if (!ctx) throw new Error('useRoles must be used inside <RolesProvider>');
  return ctx;
}
